import { ThreeJSManager } from '../core/ThreeJSManager.js';

/**
 * Particle Effects for creating and managing 3D particle systems
 */
export class ParticleEffects {
  constructor(threeJSManager) {
    this.threeJSManager = threeJSManager || new ThreeJSManager();
    this.particleSystems = new Map();
    this.particleCount = 0;
  }

  /**
   * Create a basic particle system
   * @param {Object} options - Particle system options
   * @returns {string} - Particle system ID
   */
  createParticleSystem(options = {}) {
    const {
      count = 1000,
      size = 0.1,
      color = 0xffffff,
      speed = 0.01,
      spread = 100,
      lifetime = 5000,
      texture = null,
      blending = 'additive',
      transparent = true,
      opacity = 0.8
    } = options;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);
    const lifetimes = new Float32Array(count);
    const colors = new Float32Array(count * 3);

    // Initialize particles
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      
      // Random positions within spread
      positions[i3] = (Math.random() - 0.5) * spread;
      positions[i3 + 1] = (Math.random() - 0.5) * spread;
      positions[i3 + 2] = (Math.random() - 0.5) * spread;
      
      // Random velocities
      velocities[i3] = (Math.random() - 0.5) * speed;
      velocities[i3 + 1] = (Math.random() - 0.5) * speed;
      velocities[i3 + 2] = (Math.random() - 0.5) * speed;
      
      // Random lifetimes
      lifetimes[i] = Math.random() * lifetime;
      
      // Random colors
      colors[i3] = Math.random();
      colors[i3 + 1] = Math.random();
      colors[i3 + 2] = Math.random();
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Create material
    const material = new THREE.PointsMaterial({
      size,
      color,
      transparent,
      opacity,
      vertexColors: true,
      blending: blending === 'additive' ? THREE.AdditiveBlending : THREE.NormalBlending
    });

    if (texture) {
      material.map = texture;
    }

    // Create particle system
    const particles = new THREE.Points(geometry, material);
    const id = `particles_${++this.particleCount}`;
    
    this.particleSystems.set(id, {
      particles,
      geometry,
      velocities,
      lifetimes,
      options,
      startTime: Date.now()
    });

    // Add to scene
    this.threeJSManager.scene.add(particles);
    
    return id;
  }

  /**
   * Create a fire effect
   * @param {Object} options - Fire effect options
   * @returns {string} - Particle system ID
   */
  createFireEffect(options = {}) {
    const {
      position = { x: 0, y: 0, z: 0 },
      intensity = 1,
      height = 50,
      width = 20
    } = options;

    const count = Math.floor(500 * intensity);
    const id = this.createParticleSystem({
      count,
      size: 0.3,
      color: 0xff4400,
      speed: 0.05,
      spread: width,
      lifetime: 3000,
      transparent: true,
      opacity: 0.8
    });

    const system = this.particleSystems.get(id);
    const particles = system.particles;
    
    // Position fire at specified location
    particles.position.set(position.x, position.y, position.z);
    
    // Custom update for fire effect
    system.update = (deltaTime) => {
      const positions = system.geometry.attributes.position.array;
      const velocities = system.velocities;
      const lifetimes = system.lifetimes;
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        
        // Update lifetime
        lifetimes[i] -= deltaTime * 1000;
        
        if (lifetimes[i] <= 0) {
          // Reset particle
          lifetimes[i] = Math.random() * 3000;
          positions[i3] = (Math.random() - 0.5) * width;
          positions[i3 + 1] = 0;
          positions[i3 + 2] = (Math.random() - 0.5) * width;
        } else {
          // Update position
          positions[i3] += velocities[i3];
          positions[i3 + 1] += velocities[i3 + 1] + 0.02; // Gravity effect
          positions[i3 + 2] += velocities[i3 + 2];
          
          // Add some randomness
          velocities[i3] += (Math.random() - 0.5) * 0.001;
          velocities[i3 + 2] += (Math.random() - 0.5) * 0.001;
        }
      }
      
      system.geometry.attributes.position.needsUpdate = true;
    };

    return id;
  }

  /**
   * Create a smoke effect
   * @param {Object} options - Smoke effect options
   * @returns {string} - Particle system ID
   */
  createSmokeEffect(options = {}) {
    const {
      position = { x: 0, y: 0, z: 0 },
      intensity = 1,
      height = 100,
      width = 30
    } = options;

    const count = Math.floor(300 * intensity);
    const id = this.createParticleSystem({
      count,
      size: 2,
      color: 0x666666,
      speed: 0.02,
      spread: width,
      lifetime: 8000,
      transparent: true,
      opacity: 0.3
    });

    const system = this.particleSystems.get(id);
    const particles = system.particles;
    
    particles.position.set(position.x, position.y, position.z);
    
    system.update = (deltaTime) => {
      const positions = system.geometry.attributes.position.array;
      const lifetimes = system.lifetimes;
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        
        lifetimes[i] -= deltaTime * 1000;
        
        if (lifetimes[i] <= 0) {
          lifetimes[i] = Math.random() * 8000;
          positions[i3] = (Math.random() - 0.5) * width;
          positions[i3 + 1] = 0;
          positions[i3 + 2] = (Math.random() - 0.5) * width;
        } else {
          positions[i3 + 1] += 0.01;
          positions[i3] += (Math.random() - 0.5) * 0.02;
          positions[i3 + 2] += (Math.random() - 0.5) * 0.02;
        }
      }
      
      system.geometry.attributes.position.needsUpdate = true;
    };

    return id;
  }

  /**
   * Create a sparkle effect
   * @param {Object} options - Sparkle effect options
   * @returns {string} - Particle system ID
   */
  createSparkleEffect(options = {}) {
    const {
      position = { x: 0, y: 0, z: 0 },
      count = 200,
      color = 0xffff00,
      size = 0.2
    } = options;

    const id = this.createParticleSystem({
      count,
      size,
      color,
      speed: 0.1,
      spread: 50,
      lifetime: 2000,
      transparent: true,
      opacity: 1
    });

    const system = this.particleSystems.get(id);
    const particles = system.particles;
    
    particles.position.set(position.x, position.y, position.z);
    
    system.update = (deltaTime) => {
      const positions = system.geometry.attributes.position.array;
      const lifetimes = system.lifetimes;
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        
        lifetimes[i] -= deltaTime * 1000;
        
        if (lifetimes[i] <= 0) {
          lifetimes[i] = Math.random() * 2000;
          positions[i3] = (Math.random() - 0.5) * 50;
          positions[i3 + 1] = (Math.random() - 0.5) * 50;
          positions[i3 + 2] = (Math.random() - 0.5) * 50;
        } else {
          // Sparkle movement
          positions[i3] += Math.sin(Date.now() * 0.001 + i) * 0.1;
          positions[i3 + 1] += Math.cos(Date.now() * 0.001 + i) * 0.1;
          positions[i3 + 2] += Math.sin(Date.now() * 0.002 + i) * 0.1;
        }
      }
      
      system.geometry.attributes.position.needsUpdate = true;
    };

    return id;
  }

  /**
   * Create a rain effect
   * @param {Object} options - Rain effect options
   * @returns {string} - Particle system ID
   */
  createRainEffect(options = {}) {
    const {
      intensity = 1,
      height = 200,
      width = 100
    } = options;

    const count = Math.floor(1000 * intensity);
    const id = this.createParticleSystem({
      count,
      size: 0.1,
      color: 0x88ccff,
      speed: 0.1,
      spread: width,
      lifetime: 5000,
      transparent: true,
      opacity: 0.6
    });

    const system = this.particleSystems.get(id);
    
    system.update = (deltaTime) => {
      const positions = system.geometry.attributes.position.array;
      const lifetimes = system.lifetimes;
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        
        lifetimes[i] -= deltaTime * 1000;
        
        if (lifetimes[i] <= 0) {
          lifetimes[i] = Math.random() * 5000;
          positions[i3] = (Math.random() - 0.5) * width;
          positions[i3 + 1] = height;
          positions[i3 + 2] = (Math.random() - 0.5) * width;
        } else {
          // Rain fall
          positions[i3 + 1] -= 0.5;
          
          // Reset when below ground
          if (positions[i3 + 1] < -10) {
            positions[i3 + 1] = height;
          }
        }
      }
      
      system.geometry.attributes.position.needsUpdate = true;
    };

    return id;
  }

  /**
   * Create a snow effect
   * @param {Object} options - Snow effect options
   * @returns {string} - Particle system ID
   */
  createSnowEffect(options = {}) {
    const {
      intensity = 1,
      height = 150,
      width = 120
    } = options;

    const count = Math.floor(800 * intensity);
    const id = this.createParticleSystem({
      count,
      size: 0.3,
      color: 0xffffff,
      speed: 0.02,
      spread: width,
      lifetime: 10000,
      transparent: true,
      opacity: 0.8
    });

    const system = this.particleSystems.get(id);
    
    system.update = (deltaTime) => {
      const positions = system.geometry.attributes.position.array;
      const lifetimes = system.lifetimes;
      
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        
        lifetimes[i] -= deltaTime * 1000;
        
        if (lifetimes[i] <= 0) {
          lifetimes[i] = Math.random() * 10000;
          positions[i3] = (Math.random() - 0.5) * width;
          positions[i3 + 1] = height;
          positions[i3 + 2] = (Math.random() - 0.5) * width;
        } else {
          // Snow fall with gentle swaying
          positions[i3 + 1] -= 0.1;
          positions[i3] += Math.sin(Date.now() * 0.0001 + i) * 0.02;
          positions[i3 + 2] += Math.cos(Date.now() * 0.0001 + i) * 0.02;
          
          // Reset when below ground
          if (positions[i3 + 1] < -10) {
            positions[i3 + 1] = height;
          }
        }
      }
      
      system.geometry.attributes.position.needsUpdate = true;
    };

    return id;
  }

  /**
   * Update all particle systems
   * @param {number} deltaTime - Time since last update in seconds
   */
  update(deltaTime) {
    this.particleSystems.forEach(system => {
      if (system.update) {
        system.update(deltaTime);
      }
    });
  }

  /**
   * Get particle system by ID
   * @param {string} id - Particle system ID
   * @returns {Object|null} - Particle system object
   */
  getParticleSystem(id) {
    return this.particleSystems.get(id) || null;
  }

  /**
   * Remove particle system
   * @param {string} id - Particle system ID
   */
  removeParticleSystem(id) {
    const system = this.particleSystems.get(id);
    if (system) {
      this.threeJSManager.scene.remove(system.particles);
      system.geometry.dispose();
      system.particles.material.dispose();
      this.particleSystems.delete(id);
    }
  }

  /**
   * Remove all particle systems
   */
  removeAllParticleSystems() {
    this.particleSystems.forEach(system => {
      this.threeJSManager.scene.remove(system.particles);
      system.geometry.dispose();
      system.particles.material.dispose();
    });
    this.particleSystems.clear();
  }

  /**
   * Set particle system position
   * @param {string} id - Particle system ID
   * @param {Object} position - New position {x, y, z}
   */
  setParticleSystemPosition(id, position) {
    const system = this.particleSystems.get(id);
    if (system) {
      system.particles.position.set(position.x, position.y, position.z);
    }
  }

  /**
   * Set particle system scale
   * @param {string} id - Particle system ID
   * @param {number} scale - Scale factor
   */
  setParticleSystemScale(id, scale) {
    const system = this.particleSystems.get(id);
    if (system) {
      system.particles.scale.setScalar(scale);
    }
  }

  /**
   * Get all particle system IDs
   * @returns {Array<string>} - Array of particle system IDs
   */
  getParticleSystemIds() {
    return Array.from(this.particleSystems.keys());
  }

  /**
   * Get particle system count
   * @returns {number} - Number of active particle systems
   */
  getParticleSystemCount() {
    return this.particleSystems.size;
  }
} 